import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import Joyride, {
  ACTIONS,
  type CallBackProps,
  EVENTS,
  STATUS,
  type TooltipRenderProps,
} from "react-joyride";
import { strings } from "@/I18n";
import { useTour } from "./TourProvider";
import { TourTooltip } from "./TourTooltip";
import { tourStyles } from "./tourStyles";

/**
 * Loading overlay shown during page navigation
 * Note: Avoid backdrop-blur as it creates stacking context issues with Joyride
 */
const NavigationOverlay = () => (
  <div className="tw-fixed tw-inset-0 tw-z-[1040] tw-flex tw-items-center tw-justify-center tw-bg-black/50">
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-bg-white dark:tw-bg-slate-950 tw-rounded-lg tw-p-6 tw-shadow-lg">
      <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-indigo-500" />
      <span className="tw-text-sm tw-text-gray-600">
        {strings("onboarding.loadingPage")}
      </span>
    </div>
  </div>
);

export const OnboardingTour = () => {
  const {
    isTourRunning,
    stepIndex,
    setStepIndex,
    endTour,
    steps,
    tourSteps,
    navigateToStep,
    currentRoute,
  } = useTour();

  // Track if we're in the middle of a step transition (local state for rendering control)
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isTransitioningRef = useRef(false);

  // Note: Scrolling within containers is now handled in handleStepChange
  // before the step is shown, ensuring the target is visible first

  // Handle manual navigation away from tour page
  useEffect(() => {
    if (!isTourRunning || isTransitioning) return;

    const currentStep = tourSteps[stepIndex];
    if (!currentStep) return;

    // If user manually navigated away from expected route, handle gracefully
    if (currentStep.route && currentStep.route !== currentRoute) {
      // User navigated away - we could pause tour or try to recover
      // For now, we just let the step continue (TARGET_NOT_FOUND will handle missing elements)
    }
  }, [currentRoute, isTourRunning, isTransitioning, stepIndex, tourSteps]);

  const handleStepChange = useCallback(
    async (newIndex: number) => {
      if (isTransitioningRef.current) return;

      isTransitioningRef.current = true;
      setIsTransitioning(true);

      try {
        // Navigate if needed, wait for element
        await navigateToStep(newIndex);

        // Scroll target into view within its scrollable container (e.g., sidebar)
        const step = steps[newIndex];
        if (step && step.target !== "body") {
          const target = document.querySelector(step.target as string);
          if (target) {
            // Find scrollable parent
            let parent = target.parentElement;
            while (parent && parent !== document.body) {
              const style = window.getComputedStyle(parent);
              if (
                (style.overflowY === "auto" || style.overflowY === "scroll") &&
                parent.scrollHeight > parent.clientHeight
              ) {
                // Scroll target into center of container
                const containerRect = parent.getBoundingClientRect();
                const targetRect = target.getBoundingClientRect();
                const isInView =
                  targetRect.top >= containerRect.top &&
                  targetRect.bottom <= containerRect.bottom;
                if (!isInView) {
                  const targetRelativeTop =
                    targetRect.top - containerRect.top + parent.scrollTop;
                  const scrollTo =
                    targetRelativeTop -
                    containerRect.height / 2 +
                    targetRect.height / 2;
                  parent.scrollTo({
                    top: Math.max(0, scrollTo),
                    behavior: "smooth",
                  });
                  // Wait for scroll to complete
                  await new Promise((resolve) => setTimeout(resolve, 300));
                }
                break;
              }
              parent = parent.parentElement;
            }
          }
        }

        // Update step index BEFORE showing Joyride again
        setStepIndex(newIndex);
        // Longer delay to ensure DOM is fully settled and elements are positioned correctly
        await new Promise((resolve) => setTimeout(resolve, 200));
        // Trigger a resize event to help Joyride recalculate spotlight position
        window.dispatchEvent(new Event("resize"));
      } finally {
        isTransitioningRef.current = false;
        setIsTransitioning(false);
      }
    },
    [navigateToStep, setStepIndex, steps]
  );

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, index, type, action } = data;
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      // Don't process callbacks during navigation
      if (isTransitioning || isTransitioningRef.current) return;

      // Handle close button or skip
      if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
        endTour();
        return;
      }

      // Handle step navigation (forward and back)
      if (type === EVENTS.STEP_AFTER) {
        if (action === ACTIONS.PREV) {
          const newIndex = Math.max(0, index - 1);
          handleStepChange(newIndex);
        } else if (action === ACTIONS.NEXT) {
          const newIndex = index + 1;
          handleStepChange(newIndex);
        }
      }

      // Skip to next step if target not found (element doesn't exist on this page/role)
      if (type === EVENTS.TARGET_NOT_FOUND) {
        const newIndex = index + 1;
        if (newIndex < steps.length) {
          handleStepChange(newIndex);
        } else {
          endTour();
        }
      }

      // End tour when finished or skipped
      if (finishedStatuses.includes(status as string)) {
        endTour();
      }
    },
    [endTour, handleStepChange, isTransitioning, steps.length]
  );

  // Custom tooltip wrapper that passes totalSteps
  const CustomTooltip = useCallback(
    (props: TooltipRenderProps) => (
      <TourTooltip {...props} totalSteps={steps.length} />
    ),
    [steps.length]
  );

  // Don't render Joyride while transitioning (prevents flickering and wrong step display)
  if (isTransitioning) {
    return <NavigationOverlay />;
  }

  return (
    <Joyride
      steps={steps}
      run={isTourRunning}
      stepIndex={stepIndex}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      hideCloseButton={false}
      scrollToFirstStep={false}
      disableScrolling={true}
      disableOverlayClose={true}
      spotlightClicks={true}
      spotlightPadding={12}
      scrollOffset={100}
      callback={handleCallback}
      styles={tourStyles}
      tooltipComponent={CustomTooltip}
      floaterProps={{
        styles: {
          arrow: {
            length: 10,
            spread: 14,
          },
          floater: {
            filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))",
          },
        },
        disableAnimation: false,
      }}
      locale={{
        back: strings("common.back"),
        close: strings("common.close"),
        last: strings("onboarding.getStarted"),
        next: strings("common.next"),
        skip: strings("onboarding.skipTour"),
      }}
    />
  );
};
